'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useFeatureFlag, FEATURE_FLAGS } from '@/hooks/useFeatureFlags';
import { logger } from '@/lib/logger';
import { containerFetch } from '@/lib/containerAuth';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 120_000; // 2 minutes max polling

export type BootStatus =
  | 'cold_start'
  | 'warming'
  | 'syncing_tables'
  | 'syncing_files'
  | 'ready'
  | 'error';

export interface ContainerStatusResponse {
  boot_status: string;
  ready: boolean;
  tables_synced: Record<string, number>;
  files_count: number;
  case_id: string;
  user_id: string;
  data_synced: boolean;
  message?: string;
}

export interface ContainerStatus {
  /** True while the container is not yet ready */
  isBooting: boolean;
  /** True when the container reports ready */
  isReady: boolean;
  /** True when polling has encountered an error */
  isError: boolean;
  /** Current boot phase */
  bootStatus: BootStatus | null;
  /** Fraction of tables synced (0-1) */
  tablesSync: number;
  /** Number of case files loaded */
  filesCount: number;
  /** Human-readable status message */
  message: string | null;
  /** Whether the container feature flag is enabled */
  isEnabled: boolean;
}

/**
 * Polls the case container /status endpoint every 3 seconds.
 * Only active when the 'use-case-container' feature flag is enabled.
 * Stops polling when the container reports ready or on error/timeout.
 */
export function useContainerStatus(): ContainerStatus {
  const { enabled: flagEnabled, isLoading: flagLoading } = useFeatureFlag(FEATURE_FLAGS.USE_CASE_CONTAINER);

  const [bootStatus, setBootStatus] = useState<BootStatus | null>(null);
  const [tablesSync, setTablesSync] = useState(0);
  const [filesCount, setFilesCount] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);

  const pollStartRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    // Timeout guard
    if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
      logger.warn('[ContainerStatus] Max poll duration exceeded, stopping');
      setIsError(true);
      setMessage('Container startup timed out');
      stopPolling();
      return;
    }

    try {
      const response = await containerFetch('/status', {
        signal: AbortSignal.timeout?.(5000),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data: ContainerStatusResponse = await response.json();

      // Map API response to hook state
      const tablesObj = data.tables_synced || {};
      const syncedCount = Object.values(tablesObj).filter((v) => v > 0).length;
      const totalTables = Object.keys(tablesObj).length || 14; // 14 sync tables

      if (data.ready || data.boot_status === 'ready') {
        setBootStatus('ready');
        setIsReady(true);
        setTablesSync(1);
        setFilesCount(data.files_count ?? 0);
        setMessage('AI ready');
        stopPolling();
      } else if (data.boot_status?.startsWith('error')) {
        setBootStatus('error');
        setIsError(true);
        setMessage(data.boot_status);
        stopPolling();
      } else {
        // Still booting — update progress
        const phase = syncedCount > 0 ? 'syncing_tables' : 'warming';
        setBootStatus(phase);
        setTablesSync(syncedCount / totalTables);
        setFilesCount(data.files_count ?? 0);
        setMessage(data.boot_status === 'booting' ? 'Syncing your case data...' : (data.message ?? null));
      }
    } catch (err) {
      // Network errors during cold start are expected — keep polling
      logger.debug('[ContainerStatus] Poll failed (may be cold starting):', err);
      if (!bootStatus) {
        setBootStatus('cold_start');
        setMessage('Waking up AI...');
      }
    }
  }, [bootStatus, stopPolling]);

  useEffect(() => {
    // Don't start polling if flag is off or still loading
    if (flagLoading || !flagEnabled) {
      return;
    }

    // Don't restart if already ready
    if (isReady) {
      return;
    }

    pollStartRef.current = Date.now();

    // Initial fetch immediately
    fetchStatus();

    // Then poll every POLL_INTERVAL_MS
    intervalRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);

    return () => {
      stopPolling();
    };
  }, [flagEnabled, flagLoading, isReady, fetchStatus, stopPolling]);

  const isBooting = flagEnabled && !isReady && !isError;

  return {
    isBooting,
    isReady,
    isError,
    bootStatus,
    tablesSync,
    filesCount,
    message,
    isEnabled: flagEnabled,
  };
}
